import React from 'react';
import { sortBy, isEmpty, uniq, omit, keyBy, maxBy, orderBy, fromPairs } from 'lodash';
import { format as formatDate, startOfDay, endOfDay, addMonths, addDays, } from 'date-fns';
import numeral from 'numeral';
import ellipsis from 'text-ellipsis';

import {
  statuses,
  fields as _fields,
  destinationFields,
  shipmentFields,
  reimbursementResultFields,
  discountFields,
} from '../../shared/models/troubleInquiry';
import firebase from '../../firebase';
import { areaFromPostalCode } from '../../shared/models/setting';
import { fieldDisplayValue } from '../../shared/util';
import { supportMeans as supportMeanOptions } from '../../shared/config';
import AdminPage from '../hocs/AdminPage';
import useQueryParams from '../hooks/useQueryParams';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import ExportButton from '../ExportButton';
import QueryText from '../QueryText';
import QuerySelector from '../QuerySelector';
import QueryBoolean from '../QueryBoolean';
import TenantLink from '../TenantLink';
import useTenant from '../hooks/useTenant';
import {
  troubleInquiryRef,
} from '../../models/troubleInquiry';
import { useSettingDocument, tenantAreaSettingRef } from '../../models/setting';
import { useProductTypeCollection, tenantProductTypesQuery } from '../../models/productType';
import { useProductCollection, tenantProductsQuery } from '../../models/product';
import {
  useTroubleInquiryCommentCollectionsOnce,
  tenantLimitToLastTroubleInquiryCommentsQuery,
} from '../../models/troubleInquiryComment';
import { useAgentCollection, tenantAgentsQuery } from '../../models/agent';
import { useAgentShopCollection, tenantAgentShopsGroupQuery } from '../../models/agentShop';
import QueryDateRangeSelector from '../QueryDateRangeSelector';
import SavedSearchParameterForm from '../forms/SavedSearchParameterForm';

const { keys, entries } = Object;
const db = firebase.firestore();
const ordersRef = db.collection('orders');

export default AdminPage(function AdminTroubleInquiries(props) {
  const tenant = useTenant();
  const country = tenant.salesCountry ?? 'jp';
  const { user } = props;
  const queryParams = useQueryParams();
  const {
    text = '',
    field: [field] = ['email'],
    picNames: picNamesForFilter,
    statuses: statusesForFilter,
    reimbursementOnly: _reimbursementOnly = '0',
    discountOnly: _discountOnly = '0',
    approvalRequiredOnly: _approvalRequiredOnly = '0',
    excludeReinquiry: _excludeReinquiry = '0',
    dateRange,
    displayColumns,
  } = queryParams;
  const startOn = queryParams.dateRange?.[0] ? new Date(queryParams.dateRange[0]) : addDays(addMonths(new Date(), -6), 1);
  const endOn = queryParams.dateRange?.[1] ? new Date(queryParams.dateRange[1]) : new Date();
  const reimbursementOnly = _reimbursementOnly === '1';
  const discountOnly = _discountOnly === '1';
  const approvalRequiredOnly = _approvalRequiredOnly === '1';
  const excludeReinquiry = _excludeReinquiry === '1';
  const { data: areaSetting } = useSettingDocument(tenantAreaSettingRef(tenant?.id));
  const { data: productTypes = [] } = useProductTypeCollection(tenantProductTypesQuery(tenant?.id));
  const { data: products = [] } = useProductCollection(tenantProductsQuery(tenant?.id));
  const productsById = keyBy(products, 'id');
  const fields = {
    ...omit(_fields({ country, productTypes, products }), ['destinationType']),
    ...shipmentFields({ country }),
  };
  const troubleInquiriesRef = text ? (
    db.collection('troubleInquiries')
      .where(field, '>=', text)
      .where(field, '<=', text + '\uf8ff')
      .orderBy(field, 'asc')
  ) : (
    db.collection('troubleInquiries').where('createdAt', '>=', startOfDay(startOn)).where('createdAt', '<=', endOfDay(endOn)).orderBy('createdAt', 'desc')
  );
  const troubleInquiries = useCollectionSubscriptionInTenant(troubleInquiriesRef, [text, ...[startOn, endOn].map(_ => formatDate(_, 'yyyy/MM/dd'))]);
  const staffs = useCollectionSubscriptionInTenant(db.collection('users').where('role', 'in', ['admin', 'staff']));
  const staffsById = keyBy(staffs, 'uid');
  const tenantUsers = useDocumentsFetch(
    uniq(troubleInquiries.map(_ => _.createdBy?.uid).filter(_ => _)).map(uid => tenant?.ref.collection('tenantUsers').doc(uid)), 
    [troubleInquiries.map(_ => _.createdBy?.uid).join(','), tenant?.id]
  );
  const tenantUsersByUid = keyBy(tenantUsers, 'uid');
  
  const questions = sortBy(useCollectionSubscriptionInTenant(db.collection('questions')), (_) => _.createdAt.toDate());
  const surveys = useCollectionSubscriptionInTenant(db.collection('surveys').where('surveyGroupId', 'in', ['troubleInquiry', 'troubleReinquiry']));
  const surveysById = keyBy(surveys, 'id');
  const answeredSurveys = uniq(troubleInquiries.flatMap((troubleInquiry) => {
    const product = productsById[troubleInquiry.productId];
    return [product?.troubleInquirySurveyId, product?.troubleReinquirySurveyId].filter(_ => _);
  })).map(_ => surveysById[_]);
  const answeredQuestionIds = troubleInquiries.map(_ => Object.keys(_.answers | {})).flat()
  const relatedQuestionIds = uniq([...answeredSurveys.flatMap(_ => _?.questionRows?.map(_ => _.questionId) || []), ...answeredQuestionIds].flat());
  const relatedQuestions = questions.filter(_ => relatedQuestionIds.includes(_.id));
  const lastCommentQueries = tenant
    ? troubleInquiries.map(({ id }) =>
        tenantLimitToLastTroubleInquiryCommentsQuery(troubleInquiryRef(id), tenant.id, 1)
      )
    : [];
  const { data: lastComments = [] } = useTroubleInquiryCommentCollectionsOnce(lastCommentQueries);
  const lastCommentsByTroubleInquiryId = keyBy(lastComments, ({ ref }) => ref.parent.parent.id);
  const { data: agentShops = [] } = useAgentShopCollection(tenantAgentShopsGroupQuery(tenant?.id));
  const agentShopsById = keyBy(agentShops, 'id');
  const { data: agents = [] } = useAgentCollection(tenantAgentsQuery(tenant?.id));
  const agentsById = keyBy(agents, 'id');
  const rows = troubleInquiries.map((troubleInquiry) => {
    const { items, destinationPostalCode, respondedBy, wholesaleAgentShopId, wholesaleAgentId, createdBy } = troubleInquiry;
    const area = areaFromPostalCode(destinationPostalCode, areaSetting);
    const agentShop = agentShopsById[wholesaleAgentShopId];
    const agent = agentsById[wholesaleAgentId];
    const itemsWithProduct = items.map((item) => {
      const { productId } = item;
      const product = productsById[productId];
      return { ...item, product };
    });
    const picName = respondedBy?.displayName || area?.user.displayName;
    const { body: lastComment = '' } = lastCommentsByTroubleInquiryId[troubleInquiry.id] || {};
    const tenantUser = tenantUsersByUid[createdBy?.uid];
    const accountAssignee = staffsById[tenantUser?.assigneeId];
    return {
      troubleInquiry,
      agentShop,
      agent,
      itemsWithProduct,
      area,
      picName,
      lastComment: lastComment,
      accountAssignee,
    };
  });
  const picNameOptions = uniq(rows.map((_) => _.picName))
    .filter((_) => _)
    .map((_) => ({ label: _, value: _ }));
  const statusOptions = entries(statuses).map(([value, { label }]) => ({
    label,
    value,
  }));
  const fieldOptions = [
    { label: 'メールアドレス', value: 'email' },
    { label: 'アカウントID', value: 'createdBy.uid' },
  ];
  const columnDefinitions = {
    status: { 
      label: '状態',
      renderHeader: () => <th key="status" style={{ minWidth: 80 }}>状態</th>,
      renderCell: ({ troubleInquiry, statusLabel, color, shipmentOrder, approvalStatus }) => (
        <td key="status">
          <span className={`badge badge-${color || 'secondary'}`}>{statusLabel}</span>
          {
            troubleInquiry?.reimbursementRequest && !troubleInquiry?.reimbursementResult && (
              <span className="badge badge-info">立替申請中</span>
            )
          }
          {shipmentOrder
            ? (shipmentOrder?.cammacsStatus === 'shipped'
              ? <span className="badge badge-success">発送完了</span>
              : <span className="badge badge-info">発送依頼中</span>)
            : ''
          }
          {approvalStatus === 'requested' && <span className="badge badge-danger">承認待ち</span>}
        </td>
      ),
      exportValue: _ => _.troubleInquiry.status
    },
    createdAt: {
      label: 'お問合せ日時',
      renderHeader: () => <th key="createdAt" style={{ minWidth: 150 }}>お問合せ日時</th>,
      renderCell: ({ troubleInquiry }) => (
        <td key="createdAt">{formatDate(troubleInquiry.createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}</td>
      ),
      exportValue: _ => formatDate(_.troubleInquiry.createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')
    },
    sourceOrderedAt: {
      label: '元受注日時',
      renderHeader: () => <th key="sourceOrderedAt" style={{ minWidth: 150 }}>元受注日時</th>,
      renderCell: ({ troubleInquiry }) => (
        <td key="sourceOrderedAt">
          {troubleInquiry.sourceOrder != null && formatDate(troubleInquiry.sourceOrder.createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}
        </td>
      ),
      exportValue: _ => _.troubleInquiry.sourceOrder != null && formatDate(_.troubleInquiry.sourceOrder.createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')
    },
    picName: {
      label: '担当',
      renderHeader: () => <th key="picName" style={{ minWidth: 150 }}>担当</th>,
      renderCell: ({ picName }) => <td key="picName">{picName}</td>,
      exportValue: _ => _.picName
    },
    accountAssignee: {
      label: 'アカウント担当',
      renderHeader: () => <th key="accountAssignee" style={{ minWidth: 150 }}>アカウント担当</th>,
      renderCell: ({ accountAssignee }) => <td key="accountAssignee">{accountAssignee?.displayName || '設定なし'}</td>,
      exportValue: _ => _.accountAssignee?.displayName || '設定なし'
    },
    agent: {
      label: '代理店',
      renderHeader: () => <th key="agent" style={{ minWidth: 150 }}>代理店</th>,
      renderCell: ({ agent }) => <td key="agent">{agent?.name}</td>,
      exportValue: _ => _.agent?.name
    },
    agentShop: {
      label: '店舗',
      renderHeader: () => <th key="agentShop" style={{ minWidth: 150 }}>店舗</th>,
      renderCell: ({ agentShop }) => <td key="agentShop">{agentShop?.name}</td>,
      exportValue: _ => _.agentShop?.name
    },
    prefecture: {
      label: '都道府県',
      renderHeader: () => <th key="prefecture" style={{ minWidth: 150 }}>都道府県</th>,
      renderCell: ({ troubleInquiry, fields }) => (
        <td key="prefecture">{ellipsis(fieldDisplayValue(troubleInquiry.prefecture, fields.prefecture), 80)}</td>
      ),
      exportValue: _ => fieldDisplayValue(_.troubleInquiry.prefecture, _.fields.prefecture)
    },
    city: {
      label: '市区町村',
      renderHeader: () => <th key="city" style={{ minWidth: 150 }}>市区町村</th>,
      renderCell: ({ troubleInquiry, fields }) => (
        <td key="city">{ellipsis(fieldDisplayValue(troubleInquiry.city, fields.city), 80)}</td>
      ),
      exportValue: _ => fieldDisplayValue(_.troubleInquiry.city, _.fields.city)
    },
    name: {
      label: 'お名前',
      renderHeader: () => <th key="name" style={{ minWidth: 150 }}>お名前</th>,
      renderCell: ({ troubleInquiry, fields }) => (
        <td key="name">
          <TenantLink to={`/admin/users/${troubleInquiry.createdBy.uid}`}>
            {fieldDisplayValue(troubleInquiry.name, fields.name)}
          </TenantLink>
        </td>
      ),
      exportValue: _ => fieldDisplayValue(_.troubleInquiry.name, _.fields.name)
    },
    productTypeId: {
      label: '商品種別',
      renderHeader: () => <th key="productTypeId" style={{ minWidth: 150 }}>商品種別</th>,
      renderCell: ({ troubleInquiry, fields }) => (
        <td key="productTypeId">{ellipsis(fieldDisplayValue(troubleInquiry.productTypeId, fields.productTypeId), 80)}</td>
      ),
      exportValue: _ => fieldDisplayValue(_.troubleInquiry.productTypeId, _.fields.productTypeId)
    },
    productId: {
      label: '商品',
      renderHeader: () => <th key="productId" style={{ minWidth: 150 }}>商品</th>,
      renderCell: ({ troubleInquiry, fields }) => (
        <td key="productId">{ellipsis(fieldDisplayValue(troubleInquiry.productId, fields.productId), 80)}</td>
      ),
      exportValue: _ => fieldDisplayValue(_.troubleInquiry.productId, _.fields.productId)
    },
    items: {
      label: 'パーツ',
      renderHeader: () => <th key="items" style={{ minWidth: 400 }}>パーツ</th>,
      renderCell: ({ troubleInquiry, itemsWithProduct }) => (
        <td key="items">
          {itemsWithProduct.map((item) => {
            const { product, quantity } = item;
            return (
              product != null && (
                <div key={product.id}>
                  {troubleInquiry.status === 'initial' && quantity > product.normalInventory && (
                    <span className="mr-1 text-danger small">[在庫不足]</span>
                  )}
                  <span>{product.code}</span>
                  <span className="ml-2">{product.name}</span>
                  <span className="ml-2">{numeral(quantity).format('0,0')}個</span>
                </div>
              )
            );
          })}
        </td>
      ),
      exportValue: _ => _.itemsWithProduct.map(item => `${item.product?.code} ${item.product?.name} ${numeral(item.quantity).format('0,0')}個`).join(', ')
    },
    shipmentMessage: {
      label: '発送メールでのユーザーへのメッセージ',
      renderHeader: () => <th key="shipmentMessage" style={{ minWidth: 400 }}>発送メールでのユーザーへのメッセージ</th>,
      renderCell: ({ troubleInquiry, fields }) => (
        <td key="shipmentMessage">{ellipsis(fieldDisplayValue(troubleInquiry.shipmentMessage, fields.shipmentMessage), 80)}</td>
      ),
      exportValue: _ => fieldDisplayValue(_.troubleInquiry.shipmentMessage, _.fields.shipmentMessage)
    },
    purchaseYear: {
      label: '購入時期(年)',
      renderHeader: () => <th key="purchaseYear" style={{ minWidth: 150 }}>購入時期(年)</th>,
      renderCell: ({ troubleInquiry, fields }) => (
        <td key="purchaseYear">{ellipsis(fieldDisplayValue(troubleInquiry.purchaseYear, fields.purchaseYear), 80)}</td>
      ),
      exportValue: _ => fieldDisplayValue(_.troubleInquiry.purchaseYear, _.fields.purchaseYear)
    },
    purchaseMonth: {
      label: '購入時期(月)',
      renderHeader: () => <th key="purchaseMonth" style={{ minWidth: 150 }}>購入時期(月)</th>,
      renderCell: ({ troubleInquiry, fields }) => (
        <td key="purchaseMonth">{ellipsis(fieldDisplayValue(troubleInquiry.purchaseMonth, fields.purchaseMonth), 80)}</td>
      ),
      exportValue: _ => fieldDisplayValue(_.troubleInquiry.purchaseMonth, _.fields.purchaseMonth)
    },
    phone: {
      label: '電話番号',
      renderHeader: () => <th key="phone" style={{ minWidth: 150 }}>電話番号</th>,
      renderCell: ({ troubleInquiry, fields }) => (
        <td key="phone">{ellipsis(fieldDisplayValue(troubleInquiry.phone, fields.phone), 80)}</td>
      ),
      exportValue: _ => fieldDisplayValue(_.troubleInquiry.phone, _.fields.phone)
    },
    email: {
      label: 'メールアドレス',
      renderHeader: () => <th key="email" style={{ minWidth: 150 }}>メールアドレス</th>,
      renderCell: ({ troubleInquiry, fields }) => (
        <td key="email">{ellipsis(fieldDisplayValue(troubleInquiry.email, fields.email), 80)}</td>
      ),
      exportValue: _ => fieldDisplayValue(_.troubleInquiry.email, _.fields.email)
    },
    postalCode: {
      label: '郵便番号',
      renderHeader: () => <th key="postalCode" style={{ minWidth: 150 }}>郵便番号</th>,
      renderCell: ({ troubleInquiry, fields }) => (
        <td key="postalCode">{ellipsis(fieldDisplayValue(troubleInquiry.postalCode, fields.postalCode), 80)}</td>
      ),
      exportValue: _ => fieldDisplayValue(_.troubleInquiry.postalCode, _.fields.postalCode)
    },
    address: {
      label: '番地・建物名',
      renderHeader: () => <th key="address" style={{ minWidth: 150 }}>番地・建物名</th>,
      renderCell: ({ troubleInquiry, fields }) => (
        <td key="address">{ellipsis(fieldDisplayValue(troubleInquiry.address, fields.address), 80)}</td>
      ),
      exportValue: _ => fieldDisplayValue(_.troubleInquiry.address, _.fields.address)
    },
    destination: {
      label: '配送先',
      renderHeader: () => entries(destinationFields({ country })).map(([fieldName, { label }]) => (
        <th key={fieldName} style={{ minWidth: 150 }}>配送先{label}</th>
      )),
      renderCell: ({ troubleInquiry, fields, country }) => keys(destinationFields({ country })).map((fieldName) => (
        <td key={fieldName}>
          {ellipsis(fieldDisplayValue(troubleInquiry[fieldName], fields[fieldName]), 80)}
        </td>
      )),
      exportValue: _ => keys(destinationFields({ country: _.country })).map(fieldName => fieldDisplayValue(_.troubleInquiry[fieldName], _.fields[fieldName])).join(', ')
    },
    reimbursement: {
      label: '立替',
      renderHeader: () => entries(reimbursementResultFields()).map(([fieldName, { label }]) => {
        const minWidth = fieldName === 'note' ? 400 : 150;
        return <th key={fieldName} style={{ minWidth }}>{label}</th>;
      }),
      renderCell: ({ troubleInquiry }) => entries(reimbursementResultFields()).map(([fieldName, fieldSetting]) => (
        <td key={fieldName}>
          {fieldDisplayValue(troubleInquiry.reimbursementResult?.[fieldName], fieldSetting)}
        </td>
      )),
      exportValue: _ => entries(reimbursementResultFields()).map(([fieldName, fieldSetting]) => 
        fieldDisplayValue(_.troubleInquiry.reimbursementResult?.[fieldName], fieldSetting)
      ).join(', ')
    },
    discount: {
      label: '割引',
      renderHeader: () => entries(discountFields()).map(([fieldName, { label }]) => {
        const minWidth = fieldName === 'note' ? 400 : 150;
        return <th key={fieldName} style={{ minWidth }}>{label}</th>;
      }),
      renderCell: ({ troubleInquiry }) => entries(discountFields()).map(([fieldName, fieldSetting]) => (
        <td key={fieldName}>
          {fieldDisplayValue(troubleInquiry.discount?.[fieldName], fieldSetting)}
        </td>
      )),
      exportValue: _ => entries(discountFields()).map(([fieldName, fieldSetting]) => 
        fieldDisplayValue(_.troubleInquiry.discount?.[fieldName], fieldSetting)
      ).join(', ')
    },
    discountApprovedAt: {
      label: '割引 - 承認日',
      renderHeader: () => <th key="discountApprovedAt" style={{ minWidth: 150 }}>割引 - 承認日</th>,
      renderCell: ({ troubleInquiry }) => (
        <td key="discountApprovedAt">
          {troubleInquiry.discountRequest?.approvedAt &&
            formatDate(troubleInquiry.discountRequest.approvedAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}
        </td>
      ),
      exportValue: _ => _.troubleInquiry.discountRequest?.approvedAt && formatDate(_.troubleInquiry.discountRequest.approvedAt.toDate(), 'yyyy/MM/dd HH:mm:ss')
    },
    discountApprovalComment: {
      label: '割引 - 承認/否認コメント',
      renderHeader: () => <th key="discountApprovalComment" style={{ minWidth: 400 }}>割引 - 承認/否認コメント</th>,
      renderCell: ({ troubleInquiry }) => (
        <td key="discountApprovalComment">{troubleInquiry.discountRequest?.approvalOrRejectionComment}</td>
      ),
      exportValue: _ => _.troubleInquiry.discountRequest?.approvalOrRejectionComment
    },
    approvedAt: {
      label: '承認日',
      renderHeader: () => <th key="approvedAt" style={{ minWidth: 150 }}>承認日</th>,
      renderCell: ({ troubleInquiry }) => (
        <td key="approvedAt">{troubleInquiry.approvedAt && formatDate(troubleInquiry.approvedAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}</td>
      ),
      exportValue: _ => _.troubleInquiry.approvedAt && formatDate(_.troubleInquiry.approvedAt.toDate(), 'yyyy/MM/dd HH:mm:ss')
    },
    approvalRequestComment: {
      label: '承認申請コメント',
      renderHeader: () => <th key="approvalRequestComment" style={{ minWidth: 400 }}>承認申請コメント</th>,
      renderCell: ({ troubleInquiry }) => <td key="approvalRequestComment">{troubleInquiry.approvalRequestComment}</td>,
      exportValue: _ => _.troubleInquiry.approvalRequestComment
    },
    approvalOrRejectionComment: {
      label: '承認/否認コメント',
      renderHeader: () => <th key="approvalOrRejectionComment" style={{ minWidth: 400 }}>承認/否認コメント</th>,
      renderCell: ({ troubleInquiry }) => <td key="approvalOrRejectionComment">{troubleInquiry.approvalOrRejectionComment}</td>,
      exportValue: _ => _.troubleInquiry.approvalOrRejectionComment
    },
    lastComment: {
      label: '対応コメント',
      renderHeader: () => <th key="lastComment" style={{ minWidth: 400 }}>対応コメント</th>,
      renderCell: ({ lastComment }) => <td key="lastComment">{lastComment}</td>,
      exportValue: _ => _.lastComment
    },
  };
  const questionColumnDefinitions = fromPairs(relatedQuestions.map(question => [
    `question_${question.id}`,
    {
      label: question.name,
      renderHeader: () => {
        const { id, name, type } = question;
        return (
          <th key={id} style={{ ...(type === 'text' ? { minWidth: 300 } : { minWidth: 200, maxWidth: 300 }) }}>
            {name}
          </th>
        );
      },
      renderCell: ({ troubleInquiry }) => {
        const { id, type } = question;
        const answer = troubleInquiry.answers?.[id];
        const displayValue = {
          text: ellipsis(answer ?? '', 80),
          checkbox: keys(answer || {}).join(', '),
          radio: answer,
          select: answer,
          imageFile: [answer].flat().map((answer, i) => (
            <div key={i}>
              <a href={answer?.url} target="_blank">
                {answer?.name}
              </a>
            </div>
          )),
        }[type];
        return <td key={id}>{displayValue}</td>;
      },
      exportValue: _ => {
        const { id, type } = question;
        const answer = _.troubleInquiry.answers?.[id];
        const value = ({
          checkbox: keys(answer || {}),
          imageFile: [answer].flat().map(file => file?.url).join('\n'),
        })[type] || answer;
        return value;
      }
    }
  ]));
  const allColumnDefinitions = { ...columnDefinitions, ...questionColumnDefinitions };
  const columnOptions = entries(allColumnDefinitions).map(([value, { label }]) => ({ value, label }));
  const columnsToShow = isEmpty(displayColumns) ? keys(allColumnDefinitions) : displayColumns;

  // NOTE: filter
  let filteredRows = rows;
  if (!isEmpty(picNamesForFilter)) {
    filteredRows = filteredRows.filter((_) => picNamesForFilter?.includes(_.picName));
  }
  if (!isEmpty(statusesForFilter)) {
    filteredRows = filteredRows.filter((_) => statusesForFilter?.includes(_.troubleInquiry.status));
  }
  if (reimbursementOnly) {
    filteredRows = filteredRows.filter((_) => _.troubleInquiry.hasReimbursement);
    filteredRows = orderBy(
      filteredRows,
      [({ troubleInquiry: { reimbursementResult } }) => reimbursementResult?.date.toDate() || 0],
      ['desc']
    );
  }
  if (discountOnly) {
    filteredRows = filteredRows.filter((_) => _.troubleInquiry.discount);
    filteredRows = orderBy(
      filteredRows,
      [({ troubleInquiry: { discount } }) => discount?.date.toDate() || 0],
      ['desc']
    );
  }
  if (approvalRequiredOnly) {
    filteredRows = filteredRows.filter((_) => _.troubleInquiry.approvalRequired);
    filteredRows = orderBy(filteredRows, [({ troubleInquiry: { approvedAt } }) => approvedAt?.toDate()], ['desc']);
  }
  if (excludeReinquiry) {
    filteredRows = filteredRows.filter((_) => !_.troubleInquiry.sourceTroubleInquiryId);
  }

  const rowsForExport = () => {
    return filteredRows.map(rowData => {
      const baseExport = { id: rowData.troubleInquiry.id };
      
      // exportValue関数で必要なプロパティを追加
      const exportProps = { ...rowData, fields, country };
      
      // 選択された順序でエクスポート
      const exportColumns = columnsToShow.reduce((x, columnKey) => {
        const column = allColumnDefinitions[columnKey];
        if (column?.exportValue) {
          return { ...x, [columnKey]: column.exportValue(exportProps) };
        }
        return x;
      }, {});
      
      return { ...baseExport, ...exportColumns };
    });
  };

  return (
    <div>
      <div className="admin-trouble-inquiries container-fluid py-5 position-relative">
        <div className="d-flex justify-content-center mb-3">
          <h4>不具合組立問合せ一覧</h4>
        </div>
        <div className="d-flex flex-wrap justify-content-between mb-2">
          <div className="d-flex gap-3">
            <QueryDateRangeSelector label="期間" defaultValue={[startOn, endOn]} paramName="dateRange" pickerProps={{ showYearDropdown: true, dropdownMode: 'select' }} disabled={!!text} />
            <div className="d-flex flex-wrap align-items-end gap-1">
              <QuerySelector
                paramName="field"
                options={fieldOptions}
                label="検索フィールド"
                defaultValue={[field]}
                isClearable={false}
              />
              <QueryText paramName="text" label="検索テキスト" />
            </div>
          </div>
          <SavedSearchParameterForm {...props} type="troubleInquiry" />
        </div>
        <div className="d-flex flex-wrap gap-1 mb-3 align-items-end">
          <QuerySelector paramName="picNames" width={250} isMulti options={picNameOptions} label="担当で絞込み" />
          <QuerySelector paramName="statuses" width={250} isMulti options={statusOptions} label="状態で絞込み" />
          <QuerySelector paramName="displayColumns" width={300} isMulti options={columnOptions} label="表示列を選択" maintainOrder />
          <QueryBoolean paramName="reimbursementOnly" label="立替有のみ表示" defaultValue={'0'} />
          <QueryBoolean paramName="discountOnly" label="割引有のみ表示" defaultValue={'0'} />
          <QueryBoolean paramName="approvalRequiredOnly" label="要承認のみ表示" defaultValue={'0'} />
          <QueryBoolean paramName="excludeReinquiry" label="再問合せを除く" defaultValue={'0'} />
        </div>
        <div className="d-flex justify-content-between align-items-end mb-3">
          <div>
            {filteredRows.length.toLocaleString()} 件
          </div>
          <div>
            <ExportButton
              className="ml-2"
              fileName="不具合組立問合せ.csv"
              rows={rowsForExport}
              hasPersonalInfo
              user={user}
            />
          </div>
        </div>
        <div className="overflow-auto">
          {rows.length > 0 ? (
            <table className="table table-bordered">
              <thead className="thead-light text-center text-nowrap">
                <tr>
                  <th style={{ minWidth: 150 }}>ID</th>
                  {columnsToShow.map(columnKey => allColumnDefinitions[columnKey]?.renderHeader()).filter(Boolean)}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((_) => <TroubleInquiryRow key={_.troubleInquiry.id} {..._} country={country} fields={fields} relatedQuestions={relatedQuestions} columnsToShow={columnsToShow} allColumnDefinitions={allColumnDefinitions} />)}
              </tbody>
            </table>
          ) : (
            <div>不具合組立問合せは未登録です</div>
          )}
        </div>
      </div>
    </div>
  );
});

const TroubleInquiryRow = ({ country, troubleInquiry, itemsWithProduct, picName, agent, agentShop, lastComment, accountAssignee, fields, relatedQuestions, columnsToShow, allColumnDefinitions }) => {
  const {
    id,
    createdAt,
    sourceOrder,
    status,
    createdBy,
    approvalStatus,
    approvedAt,
    approvalRequestComment,
    approvalOrRejectionComment,
    discountRequest,
    orderId,
  } = troubleInquiry;
  const { label: statusLabel, color } = statuses[status] || {};
  const shipmentOrder = useDocumentSubscription(orderId && ordersRef.doc(orderId), [orderId]);
  
  const cellProps = {
    troubleInquiry,
    itemsWithProduct,
    picName,
    agent,
    agentShop,
    lastComment,
    accountAssignee,
    fields,
    country,
    statusLabel,
    color,
    shipmentOrder,
    approvalStatus
  };
  
  return (
    <tr key={id}>
      <td>
        <TenantLink to={`/admin/troubleInquiries/${id}`}>{id}</TenantLink>
      </td>
      {columnsToShow.map(columnKey => allColumnDefinitions[columnKey]?.renderCell(cellProps)).filter(Boolean)}
    </tr>
  );
}
